import { useCallback, useEffect, useRef } from 'react';
import { BehaviorSubject, Subject } from 'rxjs';
/**
 * @abstract Works similarly to `React.useReducer<T, A>` but returns a referentially stable `Observable<T>` instead of `T`.
 * This is useful for passing down access to state through props or context without triggering updates to all consumers.
 * State consumers should use `useObservableValue` to subscribe to state values.
 *
 * @example
 * ```ts
 * const [state$, dispatch] = useObservableReducer(reducer, initialState);
 * // Use `useObservableValue` to subscribe to parts of the observable value
 * const state = useObservableValue(state$, identity);
 * const foo = useObservableValue(state$, state => state.foo.bar);
 * ```
 *
 * @param reducer Reducer<State, Action>: Exactly like you would pass to `React.useReducer`
 * @param initialState State: Exactly like you would pass to `React.useReducer`
 * @returns Tuple<
 *   BehaviorSubject<[State, Action]>; // Observable of state and actions
 *   Dispatch: (Action) => void; // Dispatch function to apply reactions to reducer
 * >
 */
export const useObservableReducer = (reducer, initialState) => {
    const state$ = useRef(new BehaviorSubject([initialState, null]));
    const action$ = useRef(new Subject());
    const dispatch = useCallback((action) => action$.current.next(action), []);
    useEffect(() => {
        // When action$ emits we compute the next state$ reduction
        const subscription = action$.current.subscribe(action => {
            const next = reducer(state$.current.value[0], action);
            state$.current.next([next, action]);
        });
        return () => subscription.unsubscribe();
    }, [initialState, reducer]);
    return [state$.current, dispatch];
};
/**
 * @abstract Use this to combine reducers.
 * Implementation follows this recipe:
 * ```
 * (f, g) => (r, x) => g (f (r, x), x)
 * ```
 *
 * @example
 * ```ts
 * const reducer1: Reducer<State, Action> = (state: State, action: Action) => {...};
 * const reducer2: Reducer<State, Action> = (state: State, action: Action) => {...};
 * const combined: Reducer<State, Action> = combineReducers(reducer1, reducer2);
 * const [state, dispatch] = useReducer(combined, initialState);
 * ```
 */
export const combineReducers = (reducer1, reducer2) => (state, action) => reducer2(reducer1(state, action), action);
/**
 * @abstract Use this to unfold the state$ returned by useObservableReducer and access the action
 * @param InnerState<T, A> the inner of the observable state returned by useObservableReducer
 * @returns The last action emitted
 */
export const unfoldAction = ([, action]) => action;
/**
 * @abstract Use this to unfold the state$ returned by useObservableReducer and access the state
 * @param InnerState<T, A> the inner of the observable state returned by useObservableReducer
 * @returns The last state emitted
 */
export const unfoldState = ([state]) => state;
